import type { UserIdentification } from './ExchangeData';
import type { AnalyticsService } from './AnalyticsService';
import {
	OrderPublicSchema,
	UserPublicSchema,
	type OrderPublic,
	type UserPublic
} from '@repo/shared/UserServiceTypes';
import { fetchRetry } from './utils';
import { PUBLIC_VOICE_ALIGN_WEBSITE } from '$env/static/public';
import type { AuthService } from './AuthService.svelte';

export type UserService = {
	isUserOnboardingComplete: () => Promise<boolean>;
	getUsername: () => string | null | undefined;
	getIdentification: () => UserIdentification | null | undefined;
	isPluginLicensed: () => Promise<boolean>;
	getUser: () => Promise<UserPublic>;
	getOrder: (order_id: string) => Promise<OrderPublic>;
	getOrders: () => Promise<OrderPublic[]>;
};
export function createUserService(
	authService: AuthService | undefined,
	analyticsService: AnalyticsService
): UserService {
	if (typeof window === 'undefined' || authService == undefined) {
		return createServerRenderingUserService();
	}
	return createUserServiceImpl(authService, analyticsService);
}
function createServerRenderingUserService(): UserService {
	return {
		async isUserOnboardingComplete() {
			throw Error('not available during server rendering');
		},
		getUsername() {
			return undefined;
		},
		getIdentification() {
			return undefined;
		},
		async isPluginLicensed() {
			throw Error('not available during server rendering');
		},
		async getOrder() {
			throw Error('not available during server rendering');
		},
		async getOrders() {
			throw Error('not available during server rendering');
		},
		async getUser() {
			throw Error('not available during server rendering');
		}
	};
}
function createUserServiceImpl(
	authService: AuthService,
	analyticsService: AnalyticsService
): UserService {
	let username = $state<string | null | undefined>();
	let identification = $state<UserIdentification | null | undefined>();

	authService.authReady().then(onAuthStateChanged);
	authService.onAuthStateChanged(onAuthStateChanged);

	function onAuthStateChanged() {
		const currentUser = authService.getUser();
		if (!currentUser) {
			username = null;
			identification = null;
			return;
		}
		username = currentUser.displayName ?? '';
		identification = {
			id: currentUser.uid,
			username: username,
			email: currentUser.email ?? ''
		};

		analyticsService.identifyUser(identification);
	}

	async function isPluginLicensed() {
		const user = await getUser();

		return user.entitlements?.plugin ?? false;
	}

	async function isUserOnboardingComplete() {
		const user = await getUser();

		if (user.onboarding_level === 1) {
			return true;
		}
		if (user.newsletter_opt_in === true) {
			return true;
		}
		return false;
	}

	async function getOrder(order_id: string) {
		if (authService.getUser() == null) {
			throw new Error('user not signed in');
		}
		const response = await fetchRetry(`${PUBLIC_VOICE_ALIGN_WEBSITE}/api/user/orders/${order_id}`, {
			method: 'get'
		});

		const json = await response.json();
		return OrderPublicSchema.parse(json);
	}

	async function getOrders() {
		if (authService.getUser() == null) {
			throw new Error('user not signed in');
		}
		const response = await fetchRetry(PUBLIC_VOICE_ALIGN_WEBSITE + '/api/user/orders', {
			method: 'get'
		});

		const json = await response.json();
		return OrderPublicSchema.array().parse(json);
	}

	async function getUser() {
		if (authService.getUser() == null) {
			throw new Error('user not signed in');
		}
		const response = await fetchRetry(PUBLIC_VOICE_ALIGN_WEBSITE + '/api/user', {
			method: 'get'
		});

		const json = await response.json();
		return UserPublicSchema.parse(json);
	}

	return {
		isUserOnboardingComplete,
		getUsername: () => username,
		getIdentification: () => identification,
		isPluginLicensed,
		getOrder,
		getOrders,
		getUser
	};
}
